/**
 * Slider Block
 * Self-contained slider component that can be used in any section.
 * Children of the slider block become slides.
 */

// Track initialized sliders to prevent duplicates
const initializedSliders = new WeakSet();

// Queue for sliders waiting for Swiper to load
const pendingInitializations = [];

// Store instances for resize handling
const sliderInstances = new Map();

// Breakpoint for mobile/desktop (matches Tailwind md:)
const MOBILE_BREAKPOINT = 768;

class SliderBlock {
  constructor(element) {
    this.element = element;
    this.swiper = null;
    this.swiperEl = null;
    this.contentEl = null;
    this.originalSlides = null;
    this.isInitialized = false;

    // Prevent duplicate instance creation
    if (initializedSliders.has(element)) return;
    initializedSliders.add(element);

    // Store visibility setting
    this.visibility = this.element.dataset.sliderVisibility || 'both';

    // Store instance for resize handling
    sliderInstances.set(element, this);

    // Check if should init based on visibility
    this.checkVisibilityAndInit();

    // Listen for resize
    this.handleResize = this.handleResize.bind(this);
    window.addEventListener('resize', this.handleResize);
  }

  /**
   * Check if slider should be active based on visibility setting and viewport
   */
  shouldBeActive() {
    const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
    
    switch (this.visibility) {
      case 'mobile':
        return isMobile;
      case 'desktop':
        return !isMobile;
      case 'both':
      default:
        return true;
    }
  }

  /**
   * Check visibility and init/destroy accordingly
   */
  checkVisibilityAndInit() {
    const shouldBeActive = this.shouldBeActive();

    if (shouldBeActive && !this.isInitialized) {
      this.init();
    } else if (!shouldBeActive && this.isInitialized) {
      this.destroy();
    }
  }

  /**
   * Handle window resize
   */
  handleResize() {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      this.checkVisibilityAndInit();
    }, 150);
  }

  /**
   * Initializes the Swiper slider
   */
  init() {
    const Swiper = window.Swiper;

    if (!Swiper) {
      pendingInitializations.push(this);
      return;
    }

    // Content container
    this.contentEl = this.element.querySelector('.slider-content');
    if (!this.contentEl) return;

    // Find and preserve control elements before clearing
    // First try within the slider (nested), then try in parent section (separate blocks)
    const blockId = this.element.dataset.blockId;
    let controlsElement = this.element.querySelector('[data-slider-control]');
    
    if (!controlsElement && blockId) {
      // Look for controls in the same section that target this slider
      const section = this.element.closest('[id^="shopify-section-"]');
      if (section) {
        controlsElement = section.querySelector(`[data-slider-control][data-slider-id="${blockId}"]`);
      }
      
      // If still not found, try any controls without a specific slider ID in the section
      if (!controlsElement) {
        const allControls = section?.querySelectorAll('[data-slider-control]:not([data-slider-id])');
        if (allControls && allControls.length === 1) {
          // Only auto-match if there's exactly one slider and one unassigned controls block
          const allSliders = section?.querySelectorAll('[data-slider-block]');
          if (allSliders && allSliders.length === 1) {
            controlsElement = allControls[0];
          }
        }
      }
    }

    let slides;
    
    if (this.originalSlides) {
      slides = this.originalSlides;
    } else {
      // Direct children become slides (filter out style, script, controls, and other non-content elements)
      const excludedTags = ['STYLE', 'SCRIPT', 'LINK', 'META', 'NOSCRIPT', 'TEMPLATE'];
      slides = [...this.contentEl.children].filter(el => 
        !excludedTags.includes(el.tagName) && 
        !el.hasAttribute('data-slider-control')
      );
      
      if (slides.length < 1) {
        return;
      }

      this.originalSlides = slides;
    }

    // Create Swiper markup
    const swiperEl = document.createElement('div');
    swiperEl.classList.add('slider-block-swiper', 'w-full', 'max-w-full');
    
    if (this.element.dataset.overflowHidden !== 'false') {
      swiperEl.classList.add('overflow-x-hidden');
    }
    
    swiperEl.dataset.swiperInstance = this.element.dataset.blockId;
    this.swiperEl = swiperEl;

    const wrapperEl = document.createElement('div');
    wrapperEl.classList.add('swiper-wrapper');

    // Move children into Swiper wrapper as slides
    slides.forEach(slide => {
      slide.classList.add('swiper-slide');
      wrapperEl.appendChild(slide);
    });

    swiperEl.appendChild(wrapperEl);

    // Replace content with Swiper
    this.contentEl.innerHTML = '';
    this.contentEl.appendChild(swiperEl);
    
    // Re-append controls if they were nested inside the slider
    // (If controls are outside in the section, they stay where they are)
    const wasNested = controlsElement?.parentElement === this.contentEl;
    if (controlsElement && wasNested) {
      this.element.appendChild(controlsElement);
    }

    // Get options from data attribute
    const customOptions = this.element.dataset.swiperOptions 
      ? JSON.parse(this.element.dataset.swiperOptions) 
      : {};

    // Default options
    const defaultOptions = {
      slidesPerView: 1,
      spaceBetween: 16,
      // Enable touch/drag interactions
      allowTouchMove: true,
      simulateTouch: true,
      touchRatio: 1,
      touchAngle: 45,
      grabCursor: true,
      // Improve touch responsiveness
      shortSwipes: true,
      longSwipes: true,
      longSwipesRatio: 0.5,
      longSwipesMs: 300,
      followFinger: true,
      threshold: 5,
      touchMoveStopPropagation: false,
      touchStartPreventDefault: false,
      touchStartForcePreventDefault: false,
      touchReleaseOnEdges: false,
    };

    const customPaginationColour = this.element.dataset.paginationColour;

    // Only enable loop if explicitly set to true in options
    if (slides.length > 1 && customOptions.loop === true) {
      defaultOptions.loop = true;
    }

    // Add navigation selectors (blockId already defined earlier)
    if (blockId && customOptions.navigation !== false) {
      let navSelector;
      
      if (controlsElement) {
        // Controls exist - use them for navigation
        if (controlsElement.dataset.sliderId === blockId) {
          // Controls are separate and explicitly linked
          navSelector = `[data-slider-control][data-slider-id="${blockId}"]`;
        } else if (!controlsElement.dataset.sliderId) {
          // Controls are nested or auto-matched, use the controls element directly
          // Don't use a selector string, pass the actual element's navigation buttons
          const nextBtn = controlsElement.querySelector('.slider-navigation-next');
          const prevBtn = controlsElement.querySelector('.slider-navigation-prev');
          if (nextBtn && prevBtn) {
            customOptions.navigation = {
              nextEl: nextBtn,
              prevEl: prevBtn,
            };
          }
        }
      }
      
      // If navSelector is set (separate controls), use selector strings
      if (navSelector) {
        customOptions.navigation = {
          nextEl: `${navSelector} .slider-navigation-next`,
          prevEl: `${navSelector} .slider-navigation-prev`,
        };
      }
    }

    // Check if slider-controls block is present (already found earlier)
    let controlsPaginationColour = customPaginationColour;
    
    if (controlsElement) {
      // Find pagination element
      const paginationEl = controlsElement.querySelector('.swiper-pagination');
      if (paginationEl) {
        // Get pagination type and colour from data attributes
        const paginationType = paginationEl.dataset.paginationType || 'bullets';
        controlsPaginationColour = paginationEl.dataset.paginationColour || customPaginationColour;
        
        // Override pagination config
        customOptions.pagination = {
          el: paginationEl,
          type: paginationType,
          clickable: true,
        };
        
        // Handle custom pagination type - render as styled bars
        if (paginationType === 'custom') {
          customOptions.pagination.renderCustom = (swiper, current, total) => {
            let bullets = '';
            for (let i = 1; i <= total; i++) {
              const isActive = i === current ? 'opacity-100' : 'opacity-50';
              bullets += `<span class="block w-10 h-1 rounded-full cursor-pointer transition-opacity duration-300 ${isActive}" style="background-color: ${controlsPaginationColour};" data-slide-index="${i}"></span>`;
            }
            return `<div class="flex gap-2 justify-center">${bullets}</div>`;
          };

          // Make custom bullets clickable
          this.element.addEventListener('click', (e) => {
            const bullet = e.target.closest('[data-slide-index]');
            if (bullet && this.swiper) {
              const index = parseInt(bullet.dataset.slideIndex, 10) - 1;
              this.swiper.slideTo(index);
            }
          });
        }
      }
    } else {
      // Fallback: Handle custom pagination type when no controls block (old behavior)
      if (customOptions.pagination && customOptions.pagination.type === 'custom') {
        customOptions.pagination.renderCustom = (swiper, current, total) => {
          let bullets = '';
          for (let i = 1; i <= total; i++) {
            const isActive = i === current ? 'opacity-100' : 'opacity-50';
            bullets += `<span class="block w-10 h-1 rounded-full cursor-pointer transition-opacity duration-300 ${isActive}" style="background-color: ${customPaginationColour};" data-slide-index="${i}"></span>`;
          }
          return `<div class="flex gap-2 justify-center">${bullets}</div>`;
        };

        // Make custom bullets clickable
        this.element.addEventListener('click', (e) => {
          const bullet = e.target.closest('[data-slide-index]');
          if (bullet && this.swiper) {
            const index = parseInt(bullet.dataset.slideIndex, 10) - 1;
            this.swiper.slideTo(index);
          }
        });
      }
    }

    // Initialize Swiper
    this.swiper = new Swiper(swiperEl, {
      ...defaultOptions,
      ...customOptions,
    });

    this.isInitialized = true;
  }

  /**
   * Destroys the Swiper instance and restores original markup
   */
  destroy() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    if (this.swiperEl && this.originalSlides && this.contentEl) {
      this.contentEl.innerHTML = '';
      this.originalSlides.forEach(slide => {
        slide.classList.remove('swiper-slide');
        this.contentEl.appendChild(slide);
      });
    }

    this.swiperEl = null;
    this.isInitialized = false;
  }

  /**
   * Full cleanup including event listeners
   */
  cleanup() {
    window.removeEventListener('resize', this.handleResize);
    clearTimeout(this.resizeTimeout);
    this.destroy();
  }
}

/**
 * Initialize all slider blocks on the page
 */
function initSliderBlocks() {
  const sliders = document.querySelectorAll('[data-slider-block]');
  sliders.forEach(slider => {
    new SliderBlock(slider);
  });
}

/**
 * Process pending initializations once Swiper is available
 */
function processPendingInitializations() {
  if (!window.Swiper || pendingInitializations.length === 0) return;
  
  while (pendingInitializations.length > 0) {
    const instance = pendingInitializations.shift();
    instance.init();
  }
}

/**
 * Wait for Swiper to be available
 */
function waitForSwiper() {
  if (window.Swiper) {
    processPendingInitializations();
    return;
  }

  let _swiper = window.Swiper;
  Object.defineProperty(window, 'Swiper', {
    configurable: true,
    get() {
      return _swiper;
    },
    set(value) {
      _swiper = value;
      Object.defineProperty(window, 'Swiper', {
        configurable: true,
        writable: true,
        value: value
      });
      processPendingInitializations();
    }
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initSliderBlocks();
    waitForSwiper();
  });
} else {
  initSliderBlocks();
  waitForSwiper();
}

// Re-initialize on back/forward navigation
window.addEventListener('pageshow', (e) => {
  if (e.persisted) {
    initSliderBlocks();
  }
});

// Export for potential external use
window.SliderBlock = SliderBlock;
window.initSliderBlocks = initSliderBlocks;

